"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import vinylRecord from "@/assets/images/vinyl-record.webp";
import { PlayIcon } from "@/components/spotify/play-icon";

/**
 * Peak height (% of card) for each bar of the mobile backdrop, left = lows.
 * Shaped like a real spectrum: heavy bass, a mid dip, a smaller presence
 * bump, then a roll-off under the play button. Literal values (not Math.random
 * or Math.sin) so server and client render identical inline styles.
 */
const SPECTRUM_PEAKS = [
  58, 74, 86, 92, 80, 95, 70, 84, 62, 76, 54, 68, 48, 60, 42, 56, 38, 50, 44,
  34, 46, 40, 52, 36, 58, 44, 64, 50, 42, 56, 36, 46, 30, 40, 26, 34,
];

/** Integer-only stagger so neighbouring bars never jump in lockstep. */
const SPECTRUM_BARS = SPECTRUM_PEAKS.map((peak, i) => ({
  peak,
  duration: 2.8 + ((i * 7) % 5) * 0.25,
  delay: -(((i * 11) % 9) * 0.37),
}));

/**
 * Three mask layers, intersected: slice the bars into 4px LED rows with 2px
 * gaps (fixed to the card, so bars light up row by row as they jump), fade
 * toward the top so the headline stays legible, and fade toward the right
 * so the green PLAY label isn't sitting on green bars.
 */
const SPECTRUM_MASK = [
  "repeating-linear-gradient(to top, black 0 4px, transparent 4px 6px)",
  "linear-gradient(to top, black 0%, rgba(0,0,0,0.8) 55%, rgba(0,0,0,0.3) 100%)",
  "linear-gradient(to right, black 68%, transparent 96%)",
].join(", ");

interface GenreJukeboxCtaProps {
  /** Plain-language invitation, e.g. "Hear who's playing in Los Angeles" */
  headline: string;
  artistCount: number;
  onClick: () => void;
  isEmpty?: boolean;
  cityName: string;
}

export function GenreJukeboxCta({
  headline,
  artistCount,
  onClick,
  isEmpty,
  cityName,
}: GenreJukeboxCtaProps) {
  const t = useTranslations("Jukebox");

  return (
    <div className="relative">
      <button
        onClick={onClick}
        className="group relative flex w-full cursor-pointer items-center gap-4 overflow-hidden rounded-xl border border-[#1DB954]/25 bg-[linear-gradient(140deg,#111a12_0%,#0b110c_50%,#12140a_100%)] px-4 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_14px_36px_-14px_rgba(0,0,0,0.9),0_0_44px_-16px_rgba(29,185,84,0.35)] transition-all duration-500 hover:border-[#1DB954]/45 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_18px_44px_-14px_rgba(0,0,0,0.9),0_0_56px_-14px_rgba(29,185,84,0.55)] md:gap-6 md:px-6 md:py-4"
      >
        {/* Mobile: stepped LED spectrum analyzer rising from the card floor */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-end gap-[3px] px-3 md:hidden"
          style={{
            maskImage: SPECTRUM_MASK,
            maskComposite: "intersect",
            WebkitMaskImage: SPECTRUM_MASK,
            WebkitMaskComposite: "source-in",
          }}
        >
          {SPECTRUM_BARS.map(({ peak, duration, delay }, i) => (
            <span
              key={i}
              className="flex-1 origin-bottom bg-gradient-to-t from-[#1DB954]/45 to-[#1DB954]/20 animate-eq-spectrum motion-reduce:animate-none motion-reduce:[transform:scaleY(0.45)]"
              style={{
                height: `${peak}%`,
                animationDuration: `${duration}s`,
                animationDelay: `${delay}s`,
              }}
            />
          ))}
        </div>

        {/* Desktop: sound ripples radiating from the disc across the card */}
        <div
          className="pointer-events-none absolute inset-0 hidden md:block"
          style={{
            background:
              "repeating-radial-gradient(circle at 80px 50%, rgba(29,185,84,0.07) 0 1.5px, transparent 1.5px 28px)",
            maskImage:
              "linear-gradient(to right, black 0%, rgba(0,0,0,0.45) 45%, transparent 88%)",
          }}
        />

        {/* Ambient glow behind vinyl */}
        <div className="pointer-events-none absolute -left-2 top-1/2 h-44 w-44 -translate-y-1/2 rounded-full bg-[#1DB954]/[0.1] blur-2xl transition-all duration-700 group-hover:bg-[#1DB954]/[0.18] group-hover:scale-110" />

        {/* Vinyl Record — photographed disc, spun with the same CSS keyframes.
            `unoptimized` because the custom image loader only serves R2 hosts;
            the static import still gives a hashed, immutable URL. */}
        <div className="relative z-10 hidden shrink-0 md:block">
          <Image
            src={vinylRecord}
            alt=""
            aria-hidden
            unoptimized
            className={`h-28 w-28 rounded-full ${
              isEmpty
                ? "animate-spin [animation-duration:3s] md:animate-none md:group-hover:animate-spin md:group-hover:[animation-duration:3s]"
                : "animate-spin [animation-duration:3s]"
            }`}
            style={{
              boxShadow: isEmpty ? undefined : "0 0 12px rgba(29,185,84,0.2)",
            }}
          />
        </div>

        {/* Content — headline in the page's mono/uppercase voice; body in the
            sans face. Mobile gets a one-line body, desktop the full pitch. */}
        <div className="relative z-10 min-w-0 flex-1 text-left">
          <span className="block text-balance font-mono text-[15px] font-bold uppercase leading-snug tracking-tight text-white md:text-xl">
            {headline}
          </span>
          <span className="mt-1 block max-w-[52ch] text-pretty font-sans text-xs leading-relaxed text-neutral-400 transition-colors group-hover:text-neutral-300 md:mt-2 md:text-sm">
            {isEmpty ? (
              t("ctaEmpty", { city: cityName })
            ) : (
              <>
                <span className="md:hidden">{t("ctaBodyShort", { artistCount })}</span>
                <span className="hidden md:inline">{t("ctaBody", { artistCount })}</span>
              </>
            )}
          </span>
        </div>

        {/* Action — round transport button mirroring the vinyl */}
        <span className="relative z-10 mr-1 flex shrink-0 flex-col items-center gap-1.5 md:mr-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1DB954] text-black shadow-[0_8px_28px_-8px_rgba(29,185,84,0.7)] transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_35px_rgba(29,185,84,0.55)] md:h-[4.5rem] md:w-[4.5rem]">
            <PlayIcon className="h-7 w-7 md:h-10 md:w-10" />
          </span>
          <span className="font-mono text-[10px] font-bold tracking-[0.2em] text-[#1DB954] md:text-xs">
            {isEmpty ? t("select") : t("play")}
          </span>
        </span>
      </button>
    </div>
  );
}
